import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import {
	RadioGroup,
	RadioGroupItem,
} from "@workspace/ui/components/radio-group";
import { Textarea } from "@workspace/ui/components/textarea";

import type { CloseReason } from "../../types";

const closeReasons: { value: CloseReason; label: string }[] = [
	{ value: "CustomerWalkedOut", label: "Customer walked out" },
	{ value: "CustomerDispute", label: "Customer dispute" },
	{ value: "CancelledByStaff", label: "Cancelled by staff" },
	{ value: "DuplicateOrder", label: "Duplicate order" },
	{ value: "StaffMistake", label: "Staff mistake" },
	{ value: "TestTraining", label: "Test / training" },
];

interface CloseOrderDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (reason: CloseReason, notes?: string) => Promise<void>;
}

export function CloseOrderDialog({
	isOpen,
	onOpenChange,
	onConfirm,
}: CloseOrderDialogProps) {
	const [reason, setReason] = useState<CloseReason | null>(null);
	const [notes, setNotes] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleConfirm = async () => {
		if (!reason) return;
		setIsSubmitting(true);
		try {
			await onConfirm(reason, notes || undefined);
			setReason(null);
			setNotes("");
			onOpenChange(false);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleOpenChange = (open: boolean) => {
		// HA-766 — not while the close is in flight; the dialog is its pending state.
		if (isSubmitting) return;

		if (!open) {
			setReason(null);
			setNotes("");
		}
		onOpenChange(open);
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Close Order</DialogTitle>
					<DialogDescription>
						Select a reason for closing this unpaid order.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<RadioGroup
						value={reason ?? ""}
						onValueChange={(value) => setReason(value as CloseReason)}
					>
						{closeReasons.map((r) => (
							<div key={r.value} className="flex items-center space-x-2">
								<RadioGroupItem value={r.value} id={r.value} />
								<Label htmlFor={r.value} className="cursor-pointer">
									{r.label}
								</Label>
							</div>
						))}
					</RadioGroup>

					<div className="space-y-2">
						<Label htmlFor="close-notes">Notes (optional)</Label>
						<Textarea
							id="close-notes"
							placeholder="Add any additional details..."
							value={notes}
							onChange={(e) => setNotes(e.target.value.slice(0, 500))}
							maxLength={500}
							rows={3}
						/>
						<p className="text-xs text-muted-foreground text-right">
							{notes.length}/500
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button onClick={handleConfirm} disabled={!reason || isSubmitting}>
						{isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
						Close Order
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
